import React, {useMemo, useState} from 'react';
// import Layout from './Layout';
import {eye, lookupEmptySvg} from 'assets/images/images';
import {
  EmptyState,
  Filter,
  FilterButton,
  Loader,
  PageLimit,
  Pagination,
  SearchInput,
  TableLayout,
} from 'components';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useQuery} from 'react-query';
import {fetchAccountCases} from '../../../../../requests/queries/flows';
import AppliedFilters from '../../../../../components/AppliedFilters';
import {filterOptions} from '../../utils';
import {filterOutEmptyValues} from '../../../../../helpers';
import moment from 'moment/moment';
import {getStatusTextAndStyles} from '../../../../../helpers/getStatusTextAndStyles';
import Breadcrumbs from '../../../../../components/BreadCrumbs';
import {PAGES_URL} from '../../../../../helpers/constants';
import DashboardLayout from '../../../../../components/DashboardLayout';

function SingleAccountCases() {
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [filterProps, setFilterProps] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);

  const [filters, setFilters] = useState({
    start: null,
    end: null,
    search: '',
  });
  const [currentPage, setCurrentPage] = useState('1');
  const [limit, setLimit] = useState(10);
  const {state} = useLocation();
  const {id} = useParams();
  const navigate = useNavigate();

  const getAccountIdFromPath = () => {
    const pathParts = location.pathname.split('/');
    return pathParts[3];
  };

  const handleRowClick = caseItem => {
    const accountId = getAccountIdFromPath();
    navigate(`/flows/cases/${accountId}/details/${caseItem.id}`);
  };

  const {data: accountCasesResponse, isLoading} = useQuery(
    [
      'users-accounts-cases',
      {
        account_id: state?.id || id,
        params: {...filters, limit, page: currentPage},
      },
    ],
    () =>
      fetchAccountCases({
        account_id: state?.id || id,
        params: filterOutEmptyValues({...filters, limit, page: currentPage}),
      }),
  );

  const pageClick = selected => {
    setCurrentPage(String(selected));
  };

  const CASES = useMemo(
    () =>
      (!isLoading && accountCasesResponse && accountCasesResponse?.entity) ||
      [],
    [accountCasesResponse, isLoading],
  );

  const handleFilter = selectedOptions => {
    const filterParams = filterOutEmptyValues(selectedOptions);
    const params = Object.fromEntries(
      Object.entries(filterParams).map(([key, value]) => [
        key,
        Array.isArray(value) ? value?.join(',') : value,
      ]),
    );

    setFilters(prev => ({
      ...prev,
      ...params,
      page: '1',
    }));
    setCurrentPage('1');
    setOpenFilter(false);
  };

  const handleResetFilter = () => {
    setFilters({
      start: null,
      end: null,
      search: filters.search,
    });
    setOpenFilter(false);
  };

  const handleFilterStateChange = props => {
    setFilterProps(props);
    setFiltersApplied(
      !!props && (props.filters?.length > 0 || props.dates?.length > 0),
    );
  };

  // const getCaseStatusStyles = status => {
  //   if (!status) return {text: '-', styles: 'bg-gray-200 text-gray-700'};
  //   const lowerStatus = status.toLowerCase();
  //   switch (lowerStatus) {
  //     case 'pending':
  //       return {text: status, styles: 'bg-yellow-100 text-yellow-700'};
  //     case 'approved':
  //     case 'allowed':
  //       return {text: status, styles: 'bg-green-100 text-green-700'};
  //     case 'rejected':
  //       return {text: status, styles: 'bg-red-100 text-red-700'};
  //     default:
  //       return {text: status, styles: 'bg-blue-100 text-blue-700'};
  //   }
  // };

  return (
    // <Layout pageTitle="Cases" pageIcon={profileIc}>
    <DashboardLayout
      bg="bg-white"
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Profiles Details</p>
      }
    >
      <div className="flex bg-white items-center min-h-[52px] py-2 flex-wrap gap-3 justify-between mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E3E6]">
        <Breadcrumbs
          items={[
            {
              label: 'Profile Details',
              path: PAGES_URL.PROFILED_RISK_PROFILE_DETAILS.replace(
                ':slug',
                id,
              ),
              state: {...state},
            },
            {label: id},
          ]}
        />
      </div>
      <Filter
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        handleFilter={options => handleFilter(options)}
        resetUrl={handleResetFilter}
        filterOptions={filterOptions}
        onFilterStateChange={handleFilterStateChange}
      />

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-wrap mt-7">
            <h2 className="text-base font-medium text-grey sm:text-lg">
              Cases
            </h2>
            {filtersApplied && filterProps && (
              <AppliedFilters {...filterProps} isMainPage={true} />
            )}
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <SearchInput
              placeholder="Search cases..."
              onChange={value => {
                setFilters(prev => ({...prev, search: value}));
                setCurrentPage('1');
              }}
            />
            <FilterButton
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </div>
        </div>

        <TableLayout negativeMargins className="mt-6">
          <thead className="text-xs font-semibold uppercase">
            <tr className="bg-white80">
              <th className="p-5 pl-12 font-medium text-left text-xs text-grey whitespace-nowrap w-[28%]">
                case ID
              </th>
              <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap w-[14%]">
                status
              </th>
              <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap w-[24%]">
                date created
              </th>
              <th className="p-5 pr-12 font-medium text-left text-xs text-grey whitespace-nowrap w-[10%]"></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={5}>
                {isLoading ? (
                  <Loader height={45} />
                ) : !isLoading && CASES?.length === 0 ? (
                  <EmptyState
                    body="No cases have been recorded yet. When there are, they will appear here."
                    noBtn
                    height={55}
                    src={lookupEmptySvg}
                  />
                ) : null}
              </td>
            </tr>
          </tbody>

          {!isLoading && CASES?.length > 0 && (
            <tbody className="text-sm bg-white">
              {CASES.map(caseItem => (
                <tr
                  onClick={() => handleRowClick(caseItem)}
                  key={caseItem?.id}
                  className="border-b border-grey60 hover:bg-gray-50 cursor-pointer"
                >
                  <td className="p-4 pl-12 text-sm text-body whitespace-nowrap w-[28%] uppercase">
                    {caseItem?.id || '-'}
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap w-[14%]">
                    <span
                      className={`uppercase py-1 px-2 text-[10px] rounded-full ${
                        getStatusTextAndStyles(caseItem?.status).styles
                      }`}
                    >
                      {getStatusTextAndStyles(caseItem?.status).text || '-'}
                    </span>
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap w-[24%]">
                    {caseItem?.createdAt
                      ? moment(caseItem?.createdAt).format(
                          'Do MMM YYYY, hh:mm A',
                        )
                      : '-'}
                  </td>
                  <td className="p-4 pr-12 text-sm text-body whitespace-nowrap w-[10%] text-right">
                    <img src={eye} alt="View" className="inline-block" />
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </TableLayout>

        {!isLoading && CASES?.length > 0 && (
          <div className="flex items-center justify-between flex-wrap gap-4 sm:gap-0 mt-8">
            <div className="flex items-center text-body text-sm">
              <PageLimit
                onLimitChange={newLimit => {
                  setLimit(newLimit);
                  setCurrentPage('1');
                }}
                total={accountCasesResponse && accountCasesResponse?.count}
                length={CASES?.length}
                limit={limit}
              />
            </div>

            <div className="mb-8 sm:mb-0">
              <Pagination
                total={
                  accountCasesResponse &&
                  Math.ceil(accountCasesResponse?.count / limit)
                }
                current={+currentPage}
                onPageChange={activePage => {
                  pageClick(activePage);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
    // </Layout>
  );
}

export default SingleAccountCases;
